import { z } from 'zod/v3'

export const AuditPackageSummarySchema = z.object({
  id: z.string(),
  auditCycleId: z.string(),
  status: z.enum(['generating', 'ready', 'failed']),
  fileName: z.string().nullable(),
  fileSize: z.number().nullable(),
  artifactCount: z.number().nullable(),
  manifestHash: z.string().nullable(),
  generatedBy: z.string(),
  errorMessage: z.string().nullable(),
  createdAt: z.date(),
})

export type AuditPackageSummary = z.infer<typeof AuditPackageSummarySchema>

export const ReadinessGateSchema = z.object({
  policiesApproved: z.boolean(),
  policiesApprovedCount: z.number(),
  policiesTotalCount: z.number(),
  riskAssessmentComplete: z.boolean(),
  systemDescriptionExists: z.boolean(),
  controlMatrixPopulated: z.boolean(),
  controlsTotal: z.number(),
  controlsCovered: z.number(),
  evidenceCollected: z.boolean(),
  evidenceFileCount: z.number(),
  blockers: z.array(z.string()),
  canGenerate: z.boolean(),
})

export type ReadinessGate = z.infer<typeof ReadinessGateSchema>

export const PackageStatusSchema = z.enum([
  'ready',
  'blocked',
  'needs_review',
  'stale',
  'not_started',
])

export const PackageRowStatusSchema = z.enum([
  'Ready',
  'Needs review',
  'Blocked',
  'Stale',
  'Optional',
])

export const PackageNextActionSchema = z.object({
  label: z.string(),
  reason: z.string(),
  severity: z.enum(['critical', 'warning', 'info']),
  target: z.object({
    type: z.enum(['package_row', 'test', 'policy', 'risk', 'document']),
    id: z.string().optional(),
    slug: z.string().optional(),
    href: z.string(),
  }),
})

export const PackageReadinessRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  status: PackageRowStatusSchema,
  missing: z.array(z.string()),
  warnings: z.array(z.string()),
  owner: z
    .object({
      id: z.string().nullable(),
      name: z.string().nullable(),
    })
    .nullable(),
  updatedAt: z.string().nullable(),
  primaryAction: z
    .object({
      label: z.string(),
      href: z.string(),
    })
    .nullable(),
  linkedTests: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      status: z.string(),
      taskCode: z.string().nullable(),
    })
  ),
  linkedControls: z.array(
    z.object({
      id: z.string(),
      controlId: z.string(),
      title: z.string(),
    })
  ),
  alsoSatisfies: z.array(
    z.object({
      auditCycleId: z.string(),
      packageKey: z.string(),
      rowKey: z.string(),
      label: z.string(),
    })
  ),
  readinessImpact: z.object({
    profile: z.string(),
    blocksReadiness: z.boolean(),
    reasonCode: z.string().nullable(),
    message: z.string().nullable(),
  }),
})

export const PackageReadinessSchema = z.object({
  auditCycleId: z.string(),
  packageKey: z.string(),
  label: z.string(),
  framework: z.string(),
  auditType: z.string(),
  status: PackageStatusSchema,
  targetAuditDate: z.string().nullable(),
  // Canonical Tests progress for the cycle's framework; package rows below
  // continue to describe deliverables and export blockers.
  testReadiness: z.object({
    total: z.number(),
    totalIncludingNA: z.number(),
    approved: z.number(),
    inProgress: z.number(),
    notApplicable: z.number(),
    readinessScore: z.number(),
  }),
  counts: z.object({
    ready: z.number(),
    blocked: z.number(),
    needsReview: z.number(),
    stale: z.number(),
    optional: z.number(),
    totalRequired: z.number(),
  }),
  controlReviewSummary: z
    .object({
      inScope: z.number(),
      notApplicable: z.number(),
      satisfactory: z.number(),
      exceptionNoted: z.number(),
      needsRemediation: z.number(),
      pending: z.number(),
      reviewProgressPct: z.number(),
    })
    .nullable(),
  nextBestAction: PackageNextActionSchema.nullable(),
  rows: z.array(PackageReadinessRowSchema),
})

export type PackageStatus = z.infer<typeof PackageStatusSchema>
export type PackageRowStatus = z.infer<typeof PackageRowStatusSchema>
export type PackageNextActionDTO = z.infer<typeof PackageNextActionSchema>
export type PackageReadinessRowDTO = z.infer<typeof PackageReadinessRowSchema>
export type PackageReadinessDTO = z.infer<typeof PackageReadinessSchema>

// ── Key documents (auditor portal) ───────────────────────────────────
// The same curated per-framework rows as `PackageReadiness`, but in delivery
// framing rather than readiness framing: an auditor asks whether a document is
// in their hands, not whether the customer's internal prep is unblocked. So
// this DTO deliberately carries NO `missing` / `warnings` / `blockerRules` /
// `readinessImpact` — those are the customer's internal state, and an external
// party reading "Blocked: 3 policies are not approved" learns something the
// engagement letter never entitled them to.

export const KeyDocumentStatusSchema = z.enum([
  /** At least one evidence artifact is attached and current. */
  'provided',
  /** Artifacts exist but are past their freshness window. */
  'stale',
  /** Nothing attached yet — the auditor's cue to ask for it. */
  'not_provided',
])

export const KeyDocumentFileSchema = z.object({
  id: z.string(),
  filename: z.string(),
  mimeType: z.string().nullable(),
  uploadedAt: z.date().nullable(),
})

export const KeyDocumentLinkSchema = z.object({
  id: z.string(),
  documentType: z.string(),
  documentTitle: z.string(),
  /** Set for `policy` links — deep-links the auditor policy reader. */
  policySlug: z.string().nullable(),
})

export const KeyDocumentSchema = z.object({
  category: z.enum(["Governance", "Security", "Engineering", "AI"]).optional(),
  requirement: z.enum(["required", "optional"]).optional(),
  key: z.string(),
  label: z.string(),
  description: z.string(),
  status: KeyDocumentStatusSchema,
  updatedAt: z.string().nullable(),
  files: z.array(KeyDocumentFileSchema),
  links: z.array(KeyDocumentLinkSchema),
  /** Tests (CES) this document's evidence slot resolves to — where it is uploaded. */
  linkedTestIds: z.array(z.string()),
})

export const KeyDocumentsSchema = z.object({
  auditCycleId: z.string(),
  framework: z.string(),
  auditType: z.string(),
  /** Null when no package is authored for this framework/auditType pair. */
  packageKey: z.string().nullable(),
  documents: z.array(KeyDocumentSchema),
})

export type KeyDocumentStatus = z.infer<typeof KeyDocumentStatusSchema>
export type KeyDocumentFileDTO = z.infer<typeof KeyDocumentFileSchema>
export type KeyDocumentLinkDTO = z.infer<typeof KeyDocumentLinkSchema>
export type KeyDocumentDTO = z.infer<typeof KeyDocumentSchema>
export type KeyDocumentsDTO = z.infer<typeof KeyDocumentsSchema>

// Audit period-coverage (audit command-center redesign)
// Answers "is evidence spread across the observation window, or clustered /
// gapped?" for a Type II cycle. Type I collapses to a single as-of column.
// Grouping area = SOC 2 criterion family (e.g. "CC6"); coverage cannot live on
// the Controls tree because that view is not windowed.
export const PeriodCoverageCellStateSchema = z.enum([
  'present',
  'gap',
  'future',
  'na',
])

export const PeriodCoverageBinSchema = z.object({
  key: z.string(),
  label: z.string(),
  isFuture: z.boolean(),
})

export const PeriodCoverageAreaSchema = z.object({
  key: z.string(),
  label: z.string(),
  cadence: z.enum(['recurring', 'point_in_time']),
  cells: z.array(PeriodCoverageCellStateSchema),
})

export const AuditPeriodCoverageSchema = z.object({
  auditCycleId: z.string(),
  auditType: z.string(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  applicable: z.boolean(),
  bins: z.array(PeriodCoverageBinSchema),
  areas: z.array(PeriodCoverageAreaSchema),
  gaps: z.array(
    z.object({
      areaKey: z.string(),
      areaLabel: z.string(),
      kind: z.enum(['in_period_gap', 'no_evidence']),
    })
  ),
})

export type PeriodCoverageCellState = z.infer<typeof PeriodCoverageCellStateSchema>
export type PeriodCoverageBinDTO = z.infer<typeof PeriodCoverageBinSchema>
export type PeriodCoverageAreaDTO = z.infer<typeof PeriodCoverageAreaSchema>
export type AuditPeriodCoverageDTO = z.infer<typeof AuditPeriodCoverageSchema>
